import React, { useState, useCallback, useMemo, useEffect, type RefCallback } from 'react';
import { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { FormPropsType } from '@atlassian/jira-issue-table-inline-issue-create/src/ui/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsItemExpanded } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import CreateChildButtonNew from '@atlassian/jira-issue-table-hierarchy/src/ui/create-child-button/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import CreateChildButtonOld from '../create-child-button/index.tsx';
import { useRowContext } from '../rows/row-context/index.tsx';
import messages from './messages.tsx';
import IndentedIcon from './indented-icon/index.tsx';

type BaseProps = {
	itemId: string;
	itemsConnectionId: string;
	depth: number;
	groupId?: string;
};

type CreateChildButtonProps = {
	createChildTriggerProps?: {
		onClick: () => void;
		ref: RefCallback<HTMLElement>;
	};
	hasChildren?: boolean;
	isRowHoveredOrSelected?: boolean;
	showCreateChildButton?: boolean;
	isCreateChildFormVisible?: boolean;
	isFormVisible?: boolean;
	formProps?: FormPropsType;
	isGroupButton?: boolean;
};

export type Props = CreateChildButtonProps & BaseProps;

const ICON_WIDTH = 24;
const EXTRA_PADDING_ROW_WITHOUT_EXPAND = 8;

/**
 * Use '@atlassian/jira-issue-table-hierarchy/src/ui/expand-button/index.tsx' instead
 * @deprecated delete when jira_list_hierarchy_extraction is cleaned up
 */
export const ExpandButtonWithCreateChildButton = ({
	itemId,
	itemsConnectionId,
	depth,
	hasChildren = true,
	isRowHoveredOrSelected,
	showCreateChildButton,
	createChildTriggerProps,
	isCreateChildFormVisible,
	isGroupButton,
}: BaseProps & CreateChildButtonProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInlineCreateChildOpen, setIsInlineCreateChildOpen] = useState(false);
	const { groupId } = useRowContext();
	const [isExpanded, { toggleIsItemExpanded }] = useIsItemExpanded({
		itemId,
		groupId,
		itemsConnectionId,
	});

	useEffect(() => {
		if (!isCreateChildFormVisible) {
			setIsInlineCreateChildOpen(false);
		}
	}, [isCreateChildFormVisible]);

	const onExpandClick = useCallback(() => {
		const actionSubjectId = isGroupButton ? 'groupExpandButton' : 'hierarchyExpandButton';
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', actionSubjectId);
		toggleIsItemExpanded({ itemId, itemsConnectionId, groupId }, depth);
	}, [
		createAnalyticsEvent,
		depth,
		itemId,
		itemsConnectionId,
		toggleIsItemExpanded,
		isGroupButton,
		groupId,
	]);

	const onCreateChildClick = useCallback(() => {
		const actionSubjectId = isGroupButton ? 'groupCreateChildButton' : 'hierarchyCreateChildButton';
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', actionSubjectId);
		if (!isExpanded) {
			toggleIsItemExpanded({ itemId, itemsConnectionId }, depth);
		}
		if (!isCreateChildFormVisible) {
			createChildTriggerProps?.onClick();
		}
		setIsInlineCreateChildOpen(true);
	}, [
		createAnalyticsEvent,
		createChildTriggerProps,
		depth,
		isCreateChildFormVisible,
		isExpanded,
		itemId,
		itemsConnectionId,
		toggleIsItemExpanded,
		isGroupButton,
	]);

	const label = useMemo(() => {
		if (isGroupButton) {
			return formatMessage(isExpanded ? messages.collapseGroup : messages.expandGroup);
		}
		return formatMessage(isExpanded ? messages.collapseIssue : messages.expandIssue);
	}, [formatMessage, isExpanded, isGroupButton]);

	const CreateChildButton = fg('jira_list_hierarchy_extraction')
		? CreateChildButtonNew
		: CreateChildButtonOld;

	if (hasChildren) {
		return (
			<Box
				tabIndex={isRowHoveredOrSelected ? 0 : -1}
				xcss={[cellStyle, !isGroupButton && cellPaddingStyle]}
			>
				<IndentedIcon depth={depth} />
				<Box
					xcss={cellWrapperStyles}
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{
						paddingLeft: `${ICON_WIDTH * depth}px`,
					}}
				>
					<IconButton
						spacing="compact"
						appearance="subtle"
						label={label}
						aria-expanded={isExpanded}
						testId="native-issue-table.ui.expand-button.expand-button"
						icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
						onClick={onExpandClick}
						isTooltipDisabled={false}
					/>
				</Box>
				{showCreateChildButton && !isInlineCreateChildOpen && (
					<CreateChildButton onClick={onCreateChildClick} ref={createChildTriggerProps?.ref} />
				)}
			</Box>
		);
	}
	return (
		<Box tabIndex={0} xcss={cellSizeStyle}>
			<IndentedIcon depth={depth} />
			<Box
				xcss={cellPaddingStyle} // eslint-disable-next-line jira/react/no-style-attribute
				style={{
					paddingLeft: `${ICON_WIDTH * depth + ICON_WIDTH + EXTRA_PADDING_ROW_WITHOUT_EXPAND}px`,
				}}
			>
				{showCreateChildButton && !isInlineCreateChildOpen && (
					<CreateChildButton onClick={onCreateChildClick} ref={createChildTriggerProps?.ref} />
				)}
			</Box>
		</Box>
	);
};

const cellWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const cellSizeStyle = xcss({
	height: '100%',
});

const cellStyle = xcss({
	display: 'flex',
	alignItems: 'center',
	position: 'relative',
});

const cellPaddingStyle = xcss({
	padding: 'space.100',
});
